import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Table, StatusChip, Pagination } from '../../components/Common';
import type { TableColumn } from '../../components/Common';
import { applicationsService, VendorApplication } from '../../services';
import { handleApiError } from '../../services/error.service';
import { useSmartPageSize } from '../../config/pagination';
import { PaginationResponse } from '../../types';

interface ApplicationsApprovedProps {
  onDataChange?: () => void;
  searchQuery?: string;
  applicationId?: string; // For filtering by specific applicationId from URL
}

const ApplicationsApproved: React.FC<ApplicationsApprovedProps> = ({ onDataChange, searchQuery = '', applicationId }) => {
  const [applications, setApplications] = useState<VendorApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Smart pagination based on screen size
  const [pagination, setPagination] = useState<PaginationResponse | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const smartPageSize = useSmartPageSize("applications");

  const fetchApprovedApplications = useCallback(async (page = 1, searchQuery = "") => {
    try {
      setLoading(true);
      setError(null);
      const response = await applicationsService.getApprovedApplications(page, smartPageSize);
      console.log("📊 ApplicationsApproved: Loaded", response.applications?.length || 0, "applications");
      setApplications(response.applications);
      // Convert ApplicationsPagination to PaginationResponse format (they're compatible)
      setPagination({
        hasMore: response.pagination.hasMore,
        currentPage: response.pagination.currentPage,
        totalPages: response.pagination.totalPages,
        totalItems: response.pagination.totalItems,
      } as PaginationResponse);
      // Don't override currentPage from response if it's different from what we requested
      if (response.pagination?.currentPage && response.pagination.currentPage !== page) {
        setCurrentPage(response.pagination.currentPage);
      }
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [smartPageSize]);

  // ✅ Fetch approved applications on mount and when page, search, or filter changes
  useEffect(() => {
    fetchApprovedApplications(currentPage, searchQuery);
  }, [currentPage, searchQuery, smartPageSize, fetchApprovedApplications]);

  // ✅ Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const time = date.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    return { date: `${day} ${month}`, time };
  };

  // Filter applications based on search query or applicationId (client-side filtering on current page)
  const filteredApplications = applications.filter(app => {
    // If applicationId is provided, filter by exact match
    if (applicationId) {
      return app.applicationId === applicationId || app.id === applicationId;
    }
    // Otherwise, use search query
    return (
      app.businessName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.contactName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.applicationId.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });
  
  // Show message if applicationId was provided but not found
  const applicationNotFound = applicationId && filteredApplications.length === 0 && !loading;

  // Define table columns
  const columns: TableColumn<VendorApplication>[] = [
    {
      key: 'date',
      label: 'Date',
      width: '90px',
      render: (application) => {
        const { date, time } = formatDate(application.submittedAt || application.createdAt);
        return (
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)', whiteSpace: 'pre-line' }}>
            {`${date},\n${time}`}
          </Typography>
        );
      },
    },
    {
      key: 'business',
      label: 'Business',
      width: 1,
      render: (application) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-brand-primary-value-hex)' }}>
            {application.businessName}
          </Typography>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.category}
          </Typography>
          {application.hasLicense && (
            <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-secondary-value-hex)' }}>
              Trade license
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'contact',
      label: 'Contact person',
      width: 1,
      render: (application) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-default-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.contactName}
          </Typography>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.email}
          </Typography>
          <Typography sx={{ fontSize: 'var(--palette-light-font-size-small-value)', fontWeight: 400, lineHeight: '1.4', color: 'var(--palette-light-text-primary-value-hex)' }}>
            {application.phone}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      label: '',
      width: '200px',
      align: 'right',
      render: () => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '16px' }}>
          <StatusChip status="success" label="Approved" />
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          width: '100%',
        }}
      >
        <CircularProgress sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ width: '100%', padding: '20px' }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {applicationNotFound && (
        <Alert severity="info" sx={{ marginBottom: '16px' }}>
          Application with ID "{applicationId}" not found in approved applications. It may be pending, declined, or does not exist.
        </Alert>
      )}
        <Table
          columns={columns}
          data={filteredApplications}
          keyExtractor={(item) => item.id}
          emptyMessage={applicationNotFound ? `Application "${applicationId}" not found` : "No approved applications found"}
          loading={loading}
        />
        
      {/* Pagination - Show even when loading */}
      {pagination && (pagination.totalPages ? pagination.totalPages > 0 : pagination.hasMore) && (
        <Pagination
          currentPage={currentPage}
          totalPages={pagination.totalPages || 1}
          totalItems={pagination.totalItems || applications.length}
          itemsPerPage={smartPageSize}
          onPageChange={(page) => setCurrentPage(page)}
          itemLabel="applications"
          showItemCount={true}
          showPrevNext={true}
          disabled={loading}
        />
      )}
    </Box>
  );
};

export default ApplicationsApproved;

